"use client"
import { Component } from "@/types"
import { cx } from "@/utils/cx"
import { MarketingNav } from "../../_sections/MarketingNav/MarketingNav"
import { MarketingSection } from "@/ui/blocks/layout/MarketingSection"
import { Title } from "@/ui/atoms/type/Title"
import { BodyText } from "@/ui/atoms/type/BodyText"
import { TextLink } from "@/ui/atoms/controls/TextLink"
import { useTrackPageView } from "@/hooks/_archive/global/analytics/useTrackPageView"

export const COMPONENT_NAME = "homeView"

export const presets = {}

export interface Props extends Component {}

export function TermsPage({ className, testId = COMPONENT_NAME }: Props) {
  useTrackPageView("Terms page")
  return (
    <div className={cx(``, className)} data-test={testId}>
      <MarketingSection removeYPadding>
        <MarketingNav className="" />
      </MarketingSection>
      <MarketingSection removeYPadding>
        <div className="mx-auto max-w-[720px] space-y-5">
          <Title size="xl" className="md:text-[32px] mb-8 md:mb-12">
            Terms and conditions
          </Title>

          <Title as="h2" size="sm">
            SANS Foundations: Computers, Technology, & Security Terms and Conditions
          </Title>

          <BodyText size="md" className="font-light text-text-secondary">
            By registering for an account, you (“User”) agree to comply with all relevant SANS
            Policies and Terms & Conditions. Failure to do so may result in termination of access to
            the services.
          </BodyText>

          <Title size="sm" as="h2">
            End User License
          </Title>

          <BodyText size="md" className="font-light text-text-secondary">
            As part of this program, subject to eligibility criteria and performance, the Escal
            Institute of Advanced Technologies,Inc., /dba SANS Institute ("SANS Institute" or
            "SANS") hereby grants the User a temporary license to SANS Foundations: Computers,
            Technology, & Security ("SANS Foundations"). SANS Foundations provides educational
            materials and training, labs, quizzes, and exams via an online training platform. As
            part of this training, the User may be required to apply cyber security tools in Labs
            and install a virtual machine, among other activities. With this license, the User is
            granted permission to undertake the activities covered in each of the training modules
            and labs as outlined in the platform. As addressed in the product-specific licenses and
            user guides, it is not acceptable to attempt denial of service attacks, or to
            purposefully attempt to cause damage to the infrastructure or data. It is also deemed a
            violation of acceptable use to identify flaws and to purposefully attempt to damage or
            disrupt other platform users, or their data.
          </BodyText>

          <BodyText size="md" className="font-light text-text-secondary">
            Uncovering security flaws and learning about cyber security vulnerabilities will require
            the use of certain tools that can only be used in a context where permission has been
            granted by the target party, as is provided herein. In severe circumstances where error
            cannot be blamed, violation of these terms could result in removal from the platform, or
            prosecution. If unsure of the activity being undertaken, please contact{` `}
            <TextLink href="mailto:foundations@sans.org" style="bold">
              foundations@sans.org
            </TextLink>
          </BodyText>

          <BodyText size="md" className="font-light text-text-secondary">
            The User may not copy, reproduce, distribute, re-publish, display, modify or create
            derivative works based upon all or any portion of SANS Foundations for any purpose and
            in any medium, whether printed, electronic or otherwise, without the express written
            consent of the SANS Institute
          </BodyText>

          <Title size="sm" as="h2">
            Privacy Policy
          </Title>

          <BodyText size="md" className="font-light text-text-secondary">
            SANS is committed to protecting the privacy and security of the User’s personal data.
            The SANS Privacy Policy (
            <TextLink href="https://sans.org/privacy" style="bold">
              sans.org/privacy
            </TextLink>
            ) aims to inform the User about how the SANS Institute collects, uses, and stores
            personal data.
          </BodyText>

          <BodyText size="md" className="font-light text-text-secondary">
            Consistent with the SANS Privacy Policy, the User understands that SANS will not
            disclose or share the User’s personal information or test scores and/or the results of
            any certification or assessment with anyone other than those individuals or
            organizations for whom the User has previously given written consent, and subject to the
            commitment by that organization to keep such information confidential and not to further
            disclose such information to any third party without my express written consent.
          </BodyText>

          <Title size="sm" as="h2">
            Conduct
          </Title>

          <BodyText size="md" className="font-light text-text-secondary">
            The User is expected to behave professionally. The use of obscene or sexually harassing
            language, and abusive or threatening behavior directed at SANS personnel or other users
            is not conducive to a learning environment. Improper conduct can result in terminating
            access to the services and SANS Foundations.
          </BodyText>

          <Title size="sm" as="h2">
            SPAM Filters & Email Notices
          </Title>

          <BodyText size="md" className="font-light text-text-secondary">
            SANS cannot be responsible for the User’s spam filters blocking any relevant SANS
            Foundations emails. Please ensure spam filters will allow e-mail from sans.org.
          </BodyText>

          <Title size="sm" as="h2">
            Cookie Policy
          </Title>

          <BodyText size="md" className="font-light text-text-secondary">
            SANS Foundations uses cookies as part of delivering our service, training, educational
            materials, labs, and website experience. By using SANS Foundations, the User agrees to
            the use of essential cookies that are a requirement for the platform to operate.
          </BodyText>

          <BodyText size="md" className="font-light text-text-secondary">
            The User may delete cookies or instruct their web browser to delete or refuse cookies.
            However, managing or restricting cookie behavior may prevent the User from accessing
            SANS Foundations.
          </BodyText>

          <BodyText size="md" className="font-light text-text-secondary">
            The User has the option to consent to the use of additional cookies which may be used to
            optimize the site experience, help SANS improve SANS Foundations, and for certain
            marketing activities. Declining the use of these additional cookies will not affect the
            User’s experience and/or access to SANS Foundations.
          </BodyText>

          <Title size="sm" as="h2">
            Transfer and Refund Policy
          </Title>

          <BodyText size="md" className="font-light text-text-secondary">
            Once access to SANS Foundations has been granted to the User, it cannot be transferred
            to a different account, and no refunds will be issued. If the User would like to request
            a refund prior to being granted access to SANS Foundations, a request must be submitted
            to{" "}
            <TextLink href="mailto:foundations@sans.org" style="bold">
              registration@sans.org
            </TextLink>
          </BodyText>

          <Title size="sm" as="h2">
            Indemnification
          </Title>

          <BodyText size="md" className="font-light text-text-secondary">
            SANS and the User (individually, as “Indemnifying Party”, collectively, “the Parties”)
            hereby agree to indemnify, defend and hold each other, their affiliates, and their
            officers, directors, employees, and agents (each an "Indemnified Party") harmless from
            and against any action, claim, suit, investigation or other proceeding (each, a "Claim")
            brought against an Indemnified Party by a third party based on: (a) breach of any
            applicable Law by such Indemnifying Party or (b) gross negligence or willful misconduct
            by such Indemnifying Party.
          </BodyText>

          <Title size="sm" as="h2">
            Disclaimer of Warranty and Limitation of Liability
          </Title>

          <BodyText size="md" className="font-light text-text-secondary">
            SANS disclaims any and all representations and warranties, oral or written, expressed,
            implied, or statutory, including without limitation, any implied warranties of fitness
            for a particular purpose or of merchantability. In no event shall the SANS Institute be
            liable for any incidental, indirect, consequential, exemplary, special, or punitive
            damages, whether or not foreseeable, and regardless of whether liability is based on
            agreement, tort, or otherwise.
          </BodyText>

          <BodyText size="md" className="font-light text-text-secondary">
            EXCEPT IN THE EVENT OF BREACH OF SECTION 1 (END USER LICENSE), IN NO EVENT SHALL EITHER
            PARTY, ANY OF THEIR RESPECTIVE DIRECTORS, OFFICERS, EMPLOYEES, OR AGENTS OR ANY OF THEIR
            AFFILIATES LIABILITY IN ANY MANNER ARISING UNDER THIS AGREEMENT EXCEED AN AMOUNT EQUAL
            TO THE TOTAL PAYMENT RECEIVED BY SANS FOR SERVICES UNDER THIS AGREEMENT DURING THE
            12-MONTH PERIOD IMMEDIATELY PRECEDING THE DATE WHEN CAUSE OF ACTION ARISES, INCLUDING
            ATTORNEY FEES.
          </BodyText>

          <Title size="sm" as="h2">
            Dispute Resolution
          </Title>

          <BodyText size="md" className="font-light text-text-secondary">
            The User agrees to submit any dispute to binding arbitration and all arbitration will
            take place in the State of Maryland, unless the User is purchasing under a US Federal
            Government contract. All disputes involving US Federal Govt. contracts will be resolved
            under the Contract Disputes Act of 1978.
          </BodyText>
        </div>
      </MarketingSection>
    </div>
  )
}
